import {format, isSameDay, parseISO} from 'date-fns'

import type {BuildOutScheduling} from './build-out-types'
import {previewRowAnnotation} from './preview-labels'
import {formatCadenceWeekdays} from './schedule-draft-logic'
import type {
    ChangePreview,
    PreviewOutcome,
    PreviewRow,
    ScheduleRole,
    ServiceSchedule,
    UnchangedReason,
} from './schemas'
import {pythonWeekdaysToJs} from './weekday-conversion'

const ROLE_ORDER: Record<ScheduleRole, number> = {deploy: 0, service: 1, pickup: 2}

/** Runs longer than this collapse to first row, a summary line, and last row. */
export const RUN_COLLAPSE_THRESHOLD = 4

export type PreviewTimelineEntry =
    | {kind: 'row'; key: string; row: PreviewRow}
    | {
          kind: 'run'
          key: string
          rows: PreviewRow[]
          outcome: PreviewOutcome
          reason: UnchangedReason | null
          summary: string
      }

function rowKey(row: PreviewRow): string {
    return `${row.role}-${row.startDate}-${row.jobId ?? 'new'}`
}

function runGroupKey(row: PreviewRow): string {
    return `${row.role}|${row.outcome}|${row.reason ?? ''}`
}

/** "12 more Tuesdays" when the run shares a weekday, otherwise "12 more visits". */
function runNoun(rows: PreviewRow[]): string {
    const weekdays = new Set(rows.map((row) => format(parseISO(row.startDate), 'EEEE')))

    return weekdays.size === 1 ? `${[...weekdays][0]!}s` : 'visits'
}

/**
 * Order the preview by date (anchors before services on a shared date) and fold long runs of
 * identical outcomes so a 30-visit re-stamp reads as a shape rather than a wall of rows.
 */
export function buildPreviewTimeline(
    rows: PreviewRow[],
    collapseThreshold: number = RUN_COLLAPSE_THRESHOLD,
): PreviewTimelineEntry[] {
    const sorted = [...rows].sort((left, right) => {
        if (left.startDate !== right.startDate) {
            return left.startDate < right.startDate ? -1 : 1
        }

        return ROLE_ORDER[left.role] - ROLE_ORDER[right.role]
    })

    const entries: PreviewTimelineEntry[] = []
    let index = 0

    while (index < sorted.length) {
        let end = index + 1
        while (end < sorted.length && runGroupKey(sorted[end]!) === runGroupKey(sorted[index]!)) {
            end += 1
        }

        const run = sorted.slice(index, end)
        if (run.length > collapseThreshold) {
            const first = run[0]!
            const last = run[run.length - 1]!
            const middle = run.slice(1, -1)

            entries.push({kind: 'row', key: rowKey(first), row: first})
            entries.push({
                kind: 'run',
                key: `run-${rowKey(first)}`,
                rows: middle,
                outcome: first.outcome,
                reason: first.reason,
                summary: `${middle.length} more ${runNoun(middle)} · all ${previewRowAnnotation(
                    first.outcome,
                    first.reason,
                ).toLowerCase()}`,
            })
            entries.push({kind: 'row', key: rowKey(last), row: last})
        } else {
            for (const row of run) {
                entries.push({kind: 'row', key: rowKey(row), row})
            }
        }

        index = end
    }

    return entries
}

export type PreviewApplyAction = {
    label: string
    destructive: boolean
    /** Nothing to write — apply would be a no-op. */
    empty: boolean
}

function pluralVisits(count: number): string {
    return count === 1 ? '1 visit' : `${count} visits`
}

/**
 * Name the button after what apply will actually write, so the commitment is legible before
 * the click rather than after it.
 */
export function previewApplyAction(preview: ChangePreview): PreviewApplyAction {
    const {createdCount, updatedCount, removedCount, unlinkedCount} = preview
    const total = createdCount + updatedCount + removedCount

    if (total === 0) {
        // Unlinking is still a write, so it stays applicable even with no created/updated/removed.
        return {label: 'Apply changes', destructive: false, empty: unlinkedCount === 0}
    }

    if (createdCount === total) {
        return {label: `Add ${pluralVisits(total)}`, destructive: false, empty: false}
    }

    if (removedCount === total) {
        return {label: `Remove ${pluralVisits(total)}`, destructive: true, empty: false}
    }

    if (updatedCount === total) {
        return {label: `Update ${pluralVisits(total)}`, destructive: false, empty: false}
    }

    return {label: `Apply ${total} changes`, destructive: false, empty: false}
}

export type ScheduleChangeSummaryItem = {
    label: string
    /** Null when there is no meaningful prior value to strike through. */
    from: string | null
    to: string
}

function requirementQuantities(schedule: ServiceSchedule): Map<string, number> {
    return new Map([
        ...schedule.workerRequirements.map(
            (requirement) => [requirement.typeId, requirement.quantity] as const,
        ),
        ...schedule.assetRequirements.map(
            (requirement) => [requirement.typeId, requirement.quantity] as const,
        ),
    ])
}

function draftQuantities(scheduling: BuildOutScheduling): Map<string, number> {
    const quantities = new Map<string, number>()
    for (const requirement of scheduling.serviceTemplate) {
        quantities.set(
            requirement.typeId,
            (quantities.get(requirement.typeId) ?? 0) + requirement.qty,
        )
    }

    return quantities
}

type DescribeChangesArgs = {
    schedule: ServiceSchedule
    scheduling: BuildOutScheduling
    workerTypeNames: Map<string, string>
    equipmentTypeNames: Map<string, string>
}

/**
 * The edit itself, as before → after pairs. This is what the ripple below is a consequence of,
 * so the preview leads with it rather than making the reader infer it from the row list.
 */
export function describeScheduleChanges({
    schedule,
    scheduling,
    workerTypeNames,
    equipmentTypeNames,
}: DescribeChangesArgs): ScheduleChangeSummaryItem[] {
    const items: ScheduleChangeSummaryItem[] = []

    const currentCadence = formatCadenceWeekdays(pythonWeekdaysToJs(schedule.cadenceWeekdays))
    const draftCadence = formatCadenceWeekdays(scheduling.cadence.weekdays)
    if (currentCadence !== draftCadence) {
        items.push({label: 'Cadence', from: currentCadence || 'None', to: draftCadence || 'None'})
    }

    const currentEnd = parseISO(schedule.endDate)
    if (!isSameDay(currentEnd, scheduling.endDate)) {
        items.push({
            label: 'End date',
            from: format(currentEnd, 'MMM d, yyyy'),
            to: format(scheduling.endDate, 'MMM d, yyyy'),
        })
    }

    const current = requirementQuantities(schedule)
    const draft = draftQuantities(scheduling)
    const typeIds = [...new Set([...current.keys(), ...draft.keys()])]
    const requirementItems: ScheduleChangeSummaryItem[] = []
    for (const typeId of typeIds) {
        const before = current.get(typeId) ?? 0
        const after = draft.get(typeId) ?? 0
        if (before === after) {
            continue
        }

        requirementItems.push({
            label: workerTypeNames.get(typeId) ?? equipmentTypeNames.get(typeId) ?? 'Requirement',
            from: before === 0 ? null : String(before),
            to: after === 0 ? 'Removed' : String(after),
        })
    }
    requirementItems.sort((left, right) => left.label.localeCompare(right.label))
    items.push(...requirementItems)

    if ((schedule.notes ?? '') !== scheduling.serviceNotes) {
        items.push({label: 'Service notes', from: null, to: 'Edited'})
    }

    const persistedSkipped = new Set(
        schedule.jobs
            .filter((job) => job.scheduleRole === 'service' && job.status === 'cancelled')
            .map((job) => job.startDate),
    )
    const draftSkipped = new Set(scheduling.skippedServiceDates)
    const newlySkipped = [...draftSkipped].filter((date) => !persistedSkipped.has(date)).sort()
    const restored = [...persistedSkipped].filter((date) => !draftSkipped.has(date)).sort()

    for (const date of newlySkipped) {
        items.push({
            label: 'Skip visit',
            from: null,
            to: format(parseISO(date), 'MMM d, yyyy'),
        })
    }
    for (const date of restored) {
        items.push({
            label: 'Restore visit',
            from: format(parseISO(date), 'MMM d, yyyy'),
            to: 'Active',
        })
    }

    return items
}
